import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getTickets, getTicketStats, createTicket, exportTickets,
  getTicketCategories, getTicketAssignees
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTicketMeta } from '../hooks/useTicketMeta';
import DatePicker from '../components/DatePicker';
import {
  MessageSquarePlus, MessageCircle, Clock, CheckCircle2,
  AlertCircle, ChevronRight, ChevronLeft, Plus, Search, Filter,
  Download, FileSpreadsheet, FileText, AlertTriangle, Timer, UserCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const PRIORITY_CONFIG = {
  alta: { label: 'Alta', className: 'bg-red-100 text-red-700 border-red-200' },
  media: { label: 'Média', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  baixa: { label: 'Baixa', className: 'bg-blue-100 text-blue-700 border-blue-200' },
};
const STATUS_CONFIG = {
  aberto: { label: 'Aberto', className: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle },
  em_andamento: { label: 'Em Andamento', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  resolvido: { label: 'Resolvido', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
};

const PAGE_SIZE = 10;

const isSlaBreached = (ticket) => {
  if (!ticket?.sla_deadline || ticket.status === 'resolvido') return false;
  try {
    return new Date(ticket.sla_deadline) < new Date();
  } catch { return false; }
};

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const { categories, assignees } = useTicketMeta(getTicketCategories, getTicketAssignees);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [newTicket, setNewTicket] = useState({ title: '', description: '', priority: 'media', category: 'duvida' });
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Helper que cria um onValueChange que também reseta a página
  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const buildParams = useCallback(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (statusFilter !== 'all') p.status = statusFilter;
    if (priorityFilter !== 'all') p.priority = priorityFilter;
    if (categoryFilter !== 'all') p.category = categoryFilter;
    if (assigneeFilter !== 'all') p.assigned_to = assigneeFilter;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  }, [page, statusFilter, priorityFilter, categoryFilter, assigneeFilter, startDate, endDate]);

  const fetchData = useCallback(async () => {
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        getTickets(buildParams()),
        getTicketStats()
      ]);
      setTickets(ticketsRes.data.items || []);
      setTotal(ticketsRes.data.total || 0);
      setTotalPages(ticketsRes.data.total_pages || 1);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newTicket.title || !newTicket.description) {
      toast.error('Preencha o título e a descrição'); return;
    }
    setCreating(true);
    try {
      await createTicket(newTicket);
      toast.success('Chamado aberto com sucesso');
      setCreateOpen(false);
      setNewTicket({ title: '', description: '', priority: 'media', category: 'duvida' });
      setPage(1);
      fetchData();
    } catch (e) {
      toast.error('Erro ao abrir chamado');
    } finally {
      setCreating(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (assigneeFilter !== 'all') params.assigned_to = assigneeFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await exportTickets(format, params);
      const blob = new Blob([response.data], {
        type: format === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `chamados_${stamp}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Relatório ${format === 'excel' ? 'Excel' : 'PDF'} gerado com sucesso`);
    } catch (e) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
    setAssigneeFilter('all');
    setStartDate('');
    setEndDate('');
    setSearch('');
  };

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return d; }
  };

  const filtered = tickets.filter(t =>
    !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase())
  );

  const hasActiveFilters = statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all' || assigneeFilter !== 'all' || startDate || endDate || search;

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Chamados</h1>
          <p className="text-gray-500 mt-1">{isAdmin ? 'Gerencie os chamados dos usuários' : 'Abra e acompanhe seus chamados'}</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={exporting || total === 0}
                className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold"
                data-testid="export-tickets-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Gerando...' : 'Exportar'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 bg-white border-gray-200">
              <DropdownMenuItem
                onClick={() => handleExport('excel')}
                className="text-gray-700 focus:bg-gray-100 cursor-pointer"
                data-testid="export-excel-item"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Exportar Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExport('pdf')}
                className="text-gray-700 focus:bg-gray-100 cursor-pointer"
                data-testid="export-pdf-item"
              >
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exportar PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#003366] hover:bg-[#002244] font-semibold" data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Chamado
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total || 0, icon: MessageCircle, color: 'text-[#003366]', bg: 'bg-blue-50' },
          { label: 'Abertos', value: stats.aberto || 0, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Em Andamento', value: stats.em_andamento || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Resolvidos', value: stats.resolvido || 0, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          {
            label: 'SLA Estourado',
            value: stats.sla_breached || 0,
            icon: AlertTriangle,
            color: 'text-red-700',
            bg: 'bg-red-100',
            hint: stats.avg_response_minutes != null ? `Resp. média ${stats.avg_response_minutes}min` : null
          },
        ].map((s) => (
          <Card key={s.label} className="bg-white border-gray-200 shadow-sm" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${s.bg}`}><s.icon className={`w-5 h-5 ${s.color}`} /></div>
              <div className="min-w-0">
                <p className="text-2xl font-semibold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500 truncate">{s.label}</p>
                {s.hint && <p className="text-[10px] text-gray-400">{s.hint}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Buscar chamados..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10 bg-white border-gray-300 text-gray-900" data-testid="search-tickets" />
            </div>
            <Select value={statusFilter} onValueChange={onFilterChange(setStatusFilter)}>
              <SelectTrigger className="w-[170px] bg-white border-gray-300 text-gray-700" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">Todos status</SelectItem>
                <SelectItem value="aberto">Abertos</SelectItem>
                <SelectItem value="em_andamento">Em Andamento</SelectItem>
                <SelectItem value="resolvido">Resolvidos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={onFilterChange(setPriorityFilter)}>
              <SelectTrigger className="w-[160px] bg-white border-gray-300 text-gray-700" data-testid="priority-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Prioridade" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">Toda prioridade</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="media">Média</SelectItem>
                <SelectItem value="baixa">Baixa</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={onFilterChange(setCategoryFilter)}>
              <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-700" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">Todas categorias</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={assigneeFilter} onValueChange={onFilterChange(setAssigneeFilter)}>
                <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-700" data-testid="assignee-filter">
                  <UserCircle className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Atribuído" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">Todos atribuídos</SelectItem>
                  <SelectItem value="unassigned">Não atribuído</SelectItem>
                  {assignees.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">De</Label>
                <DatePicker
                  value={startDate}
                  onChange={(v) => { setStartDate(v); setPage(1); }}
                  className="w-[210px]"
                  placeholder="Data inicial"
                  data-testid="start-date-filter"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Até</Label>
                <DatePicker
                  value={endDate}
                  onChange={(v) => { setEndDate(v); setPage(1); }}
                  className="w-[210px]"
                  placeholder="Data final"
                  data-testid="end-date-filter"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-gray-600 hover:text-gray-900" data-testid="clear-filters-btn">
                Limpar filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card className="bg-white border-gray-200 shadow-md">
        <CardHeader className="border-b border-gray-100 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2 font-semibold">
            <MessageCircle className="w-5 h-5 text-[#003366]" />
            {isAdmin ? 'Todos os Chamados' : 'Meus Chamados'}
          </CardTitle>
          <span className="text-sm text-gray-500" data-testid="tickets-total-count">{total} {total === 1 ? 'chamado' : 'chamados'}</span>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 bg-gray-100" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <MessageSquarePlus className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Nenhum chamado encontrado</p>
              <p className="text-sm mt-1">Clique em &ldquo;Novo Chamado&rdquo; para abrir um</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((ticket) => {
                const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.aberto;
                const priorityCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.media;
                const StatusIcon = statusCfg.icon;
                return (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors group"
                    data-testid={`ticket-row-${ticket.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${statusCfg.className.split(' ')[0]}`}>
                        <StatusIcon className={`w-5 h-5 ${statusCfg.className.split(' ')[1]}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 group-hover:text-[#003366] transition-colors truncate">{ticket.title}</p>
                          <Badge variant="outline" className={priorityCfg.className + ' text-xs'}>{priorityCfg.label}</Badge>
                          <Badge variant="outline" className={statusCfg.className + ' text-xs'}>{statusCfg.label}</Badge>
                          {ticket.category && (
                            <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200 text-xs">
                              {(categories.find(c => c.value === ticket.category)?.label) || ticket.category}
                            </Badge>
                          )}
                          {isSlaBreached(ticket) && (
                            <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs flex items-center gap-1" data-testid={`sla-breached-${ticket.id}`}>
                              <AlertTriangle className="w-3 h-3" />SLA Estourado
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                          {isAdmin && <span>Por: {ticket.created_by_name}</span>}
                          {ticket.assigned_to_name && (
                            <span className="flex items-center gap-1"><UserCircle className="w-3 h-3" />{ticket.assigned_to_name}</span>
                          )}
                          <span>{formatDate(ticket.created_at)}</span>
                          {ticket.sla_deadline && ticket.status !== 'resolvido' && (
                            <span className="flex items-center gap-1"><Timer className="w-3 h-3" />SLA: {formatDate(ticket.sla_deadline)}</span>
                          )}
                          {ticket.message_count > 0 && (
                            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{ticket.message_count}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#003366] transition-colors flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50/50">
            <span className="text-sm text-gray-600" data-testid="pagination-info">
              Página <span className="font-semibold text-gray-900">{page}</span> de <span className="font-semibold text-gray-900">{totalPages}</span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                data-testid="pagination-next"
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border-gray-200 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Novo Chamado</DialogTitle>
            <DialogDescription className="text-gray-500">Descreva o problema ou solicitação</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Título *</Label>
              <Input value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} placeholder="Resumo do problema" className="bg-white border-gray-300 text-gray-900" data-testid="ticket-title-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Descrição *</Label>
              <Textarea value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} placeholder="Descreva em detalhes..." rows={4} className="bg-white border-gray-300 text-gray-900 resize-none" data-testid="ticket-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Prioridade</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-700" data-testid="ticket-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="alta">Alta (SLA 4h)</SelectItem>
                    <SelectItem value="media">Média (SLA 24h)</SelectItem>
                    <SelectItem value="baixa">Baixa (SLA 72h)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Categoria</Label>
                <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-700" data-testid="ticket-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="bg-white border-gray-300 text-gray-700">Cancelar</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#003366] hover:bg-[#002244]" data-testid="submit-ticket-btn">
              {creating ? 'Enviando...' : 'Abrir Chamado'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tickets;
